import { detectarPlataforma } from "../game/saves/escolha";
import type { GuardaDeSessao } from "./cliente";

/**
 * O cofre de cada plataforma: onde ficam o login e o identificador do aparelho.
 *
 * - **Computador:** o Gerenciador de Credenciais do Windows, pela casca em Rust
 *   (`src-tauri/src/cofre.rs`).
 * - **Android:** um SQLite só dele, na pasta privada do aplicativo e fora do
 *   backup do Google (`cofre-capacitor.ts`).
 * - **Navegador:** o login é cookie `httpOnly` que o JavaScript nem enxerga, e
 *   o identificador do aparelho fica no `localStorage`.
 *
 * As chaves são só estas duas, e a casca em Rust recusa qualquer outra.
 */
export type ChaveDoCofre = "refresh" | "aparelho";

export interface Cofre {
  ler(chave: ChaveDoCofre): Promise<string | null>;
  gravar(chave: ChaveDoCofre, valor: string | null): Promise<void>;
}

const PREFIXO_LOCAL = "lm.cofre.";

/** O cofre do navegador. O refresh nunca passa por aqui: é cookie. */
const cofreDoNavegador: Cofre = {
  async ler(chave) {
    if (chave === "refresh") return null;
    try {
      return window.localStorage.getItem(PREFIXO_LOCAL + chave);
    } catch {
      return null;
    }
  },
  async gravar(chave, valor) {
    if (chave === "refresh") return;
    try {
      if (valor === null) window.localStorage.removeItem(PREFIXO_LOCAL + chave);
      else window.localStorage.setItem(PREFIXO_LOCAL + chave, valor);
    } catch {
      // Navegador sem armazenamento (aba anônima restrita): o aparelho vira
      // outro a cada abertura, e só isso.
    }
  },
};

const cofreDoTauri: Cofre = {
  async ler(chave) {
    const { invoke } = await import("@tauri-apps/api/core");
    return (await invoke<string | null>("ler_cofre", { chave })) ?? null;
  },
  async gravar(chave, valor) {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("gravar_cofre", { chave, valor });
  },
};

let cofreDoCapacitor: Promise<Cofre> | null = null;

function capacitor(): Promise<Cofre> {
  cofreDoCapacitor ??= import("./cofre-capacitor").then(({ abrirCofreCapacitor }) => abrirCofreCapacitor());
  return cofreDoCapacitor;
}

/** O cofre desta plataforma. Os nativos são carregados só onde existem. */
export function cofre(): Cofre {
  switch (detectarPlataforma()) {
    case "tauri":
      return cofreDoTauri;
    case "capacitor":
      return {
        ler: async (chave) => (await capacitor()).ler(chave),
        gravar: async (chave, valor) => (await capacitor()).gravar(chave, valor),
      };
    case "web":
      return cofreDoNavegador;
  }
}

/**
 * Guarda de sessão dos aplicativos: o refresh vai para o cofre do sistema.
 *
 * Sem isto o aplicativo recebia o refresh no corpo do login e jogava fora, e a
 * pessoa precisava entrar de novo toda vez que abria o jogo. Falha do cofre
 * vira "sem login guardado", nunca erro na tela: o pior caso é pedir a senha.
 */
export function guardaDoCofre(alvo: Cofre): GuardaDeSessao {
  return {
    lerRefresh: () => alvo.ler("refresh").catch(() => null),
    gravarRefresh: (token) => alvo.gravar("refresh", token).catch(() => undefined),
  };
}
